import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { Button } from '../../../components/primitives/Button';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Caption, Heading4, Label } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useVoiceAgentAnalytics } from '../hooks';
import type { AnalyticsPeriod } from '../types';

interface AgentAnalyticsPanelProps {
  agentId: string;
  organizationId: string;
  period: AnalyticsPeriod;
  onPeriodChange: (next: AnalyticsPeriod) => void;
}

const PERIODS: AnalyticsPeriod[] = ['7d', '30d', '90d'];

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatSeconds(seconds: number | null): string {
  if (seconds == null) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function formatMs(ms: number | null): string {
  if (ms == null) return '—';
  return `${Math.round(ms)} ms`;
}

function formatUsd(value: number | null): string {
  if (value == null) return '—';
  return `$${value.toFixed(2)}`;
}

export function AgentAnalyticsPanel({
  agentId,
  organizationId,
  period,
  onPeriodChange,
}: AgentAnalyticsPanelProps) {
  const { t } = useTranslation();
  const { data, isPending, isError, error } = useVoiceAgentAnalytics(
    agentId,
    organizationId,
    period,
  );

  const periodLabel = (p: AnalyticsPeriod) => t(`voiceAgents.analytics.period.${p}`);
  const resolveOutcomeLabel = (outcome: string) => {
    const key = `voiceAgents.analytics.outcome.${outcome}`;
    const label = t(key);
    return label === key ? outcome : label;
  };

  return (
    <VStack spacing="lg" accessibilityLabel={t('voiceAgents.analytics.panelLabel')}>
      <HStack spacing="sm" style={styles.periodRow} accessibilityRole="radiogroup">
        {PERIODS.map((p) => (
          <Button
            key={p}
            size="sm"
            variant={period === p ? 'primary' : 'outline'}
            onPress={() => onPeriodChange(p)}
            accessibilityRole="radio"
            accessibilityState={{ checked: period === p }}
            accessibilityLabel={`${t('voiceAgents.analytics.showWindowPrefix')} ${periodLabel(p)}`}
          >
            {periodLabel(p)}
          </Button>
        ))}
      </HStack>

      {isPending ? (
        <View style={styles.loading} accessibilityLabel={t('voiceAgents.analytics.loading')}>
          <ActivityIndicator color={colors.primary.main} />
        </View>
      ) : null}

      {isError ? (
        <Body style={styles.error} accessibilityLiveRegion="polite" accessibilityRole="alert">
          {t('voiceAgents.analytics.loadError')}:{' '}
          {error instanceof Error ? error.message : t('voiceAgents.analytics.unknownError')}
        </Body>
      ) : null}

      {data && data.totalCalls === 0 ? (
        <Body style={styles.empty} accessibilityLiveRegion="polite">
          {t('voiceAgents.analytics.emptyPrefix')} {periodLabel(period).toLowerCase()}.{' '}
          {t('voiceAgents.analytics.emptySuffix')}
        </Body>
      ) : null}

      {data && data.totalCalls > 0 ? (
        <>
          <View style={styles.kpiGrid}>
            <KpiCard
              label={t('voiceAgents.analytics.totalCalls')}
              value={String(data.totalCalls)}
            />
            <KpiCard
              label={t('voiceAgents.analytics.meetingBooked')}
              value={formatPercent(data.meetingBookedRate)}
              hint={t('voiceAgents.analytics.ofCallsWithOutcome')}
            />
            <KpiCard
              label={t('voiceAgents.analytics.avgDuration')}
              value={formatSeconds(data.avgDurationSeconds)}
            />
            <KpiCard
              label={t('voiceAgents.analytics.totalCost')}
              value={formatUsd(data.totalCostUsd)}
            />
            <KpiCard
              label={t('voiceAgents.analytics.costPerMeeting')}
              value={data.costPerMeeting != null ? formatUsd(data.costPerMeeting) : '—'}
              hint={
                data.costPerMeeting == null
                  ? t('voiceAgents.analytics.noMeetingsBooked')
                  : undefined
              }
            />
            <KpiCard
              label={t('voiceAgents.analytics.turnTaking')}
              value={formatMs(data.p50TurnTakingMs)}
              hint={`p95 ${formatMs(data.p95TurnTakingMs)}`}
            />
          </View>

          <VStack spacing="md">
            <Heading4 style={styles.sectionTitle}>
              {t('voiceAgents.analytics.outcomesTitle')}
            </Heading4>
            <OutcomeBars
              callsByOutcome={data.callsByOutcome}
              totalCalls={data.totalCalls}
              resolveLabel={resolveOutcomeLabel}
              emptyLabel={t('voiceAgents.analytics.noOutcomes')}
              ofLabel={t('voiceAgents.analytics.of')}
            />
          </VStack>
        </>
      ) : null}
    </VStack>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <View style={styles.kpiCard} accessibilityLabel={`${label}: ${value}`}>
      <Caption style={styles.kpiLabel}>{label}</Caption>
      <Label style={styles.kpiValue}>{value}</Label>
      {hint ? <Caption style={styles.kpiHint}>{hint}</Caption> : null}
    </View>
  );
}

function OutcomeBars({
  callsByOutcome,
  totalCalls,
  resolveLabel,
  emptyLabel,
  ofLabel,
}: {
  callsByOutcome: Record<string, number>;
  totalCalls: number;
  resolveLabel: (outcome: string) => string;
  emptyLabel: string;
  ofLabel: string;
}) {
  const entries = Object.entries(callsByOutcome).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    return <Caption style={styles.empty}>{emptyLabel}</Caption>;
  }
  return (
    <VStack spacing="sm">
      {entries.map(([outcome, count]) => {
        const pct = totalCalls > 0 ? count / totalCalls : 0;
        return (
          <View
            key={outcome}
            accessibilityLabel={`${resolveLabel(outcome)} ${count} ${ofLabel} ${totalCalls}`}
          >
            <HStack style={styles.outcomeRow} spacing="sm">
              <Body style={styles.outcomeLabel}>{resolveLabel(outcome)}</Body>
              <Body style={styles.outcomeCount}>
                {count} <Caption style={styles.outcomePercent}>({formatPercent(pct)})</Caption>
              </Body>
            </HStack>
            <View style={styles.barTrack}>
              <View
                style={[
                  styles.barFill,
                  {
                    width: `${pct * 100}%`,
                    backgroundColor: pickOutcomeColor(outcome),
                  },
                ]}
              />
            </View>
          </View>
        );
      })}
    </VStack>
  );
}

function pickOutcomeColor(outcome: string): string {
  if (outcome === 'meeting_booked' || outcome === 'qualified')
    return colors.status?.success?.main ?? '#22c55e';
  if (outcome === 'callback_requested' || outcome === 'voicemail_left')
    return colors.status?.warning?.main ?? '#f59e0b';
  if (outcome === 'technical_failure' || outcome === 'do_not_call' || outcome === 'not_qualified')
    return colors.status?.error?.main ?? '#ef4444';
  return colors.primary.main;
}

const styles = StyleSheet.create({
  periodRow: {
    alignItems: 'center',
  },
  loading: {
    paddingVertical: tokens.spacing.xl,
    alignItems: 'center',
  },
  error: {
    color: colors.status?.error?.main ?? '#f87171',
    lineHeight: 20,
  },
  empty: {
    color: colors.text.tertiary,
    lineHeight: 20,
  },
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.md,
  },
  kpiCard: {
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.mdPlus,
    minWidth: 140,
    flexGrow: 1,
    flexBasis: '30%',
    gap: tokens.spacing.xs,
  },
  kpiLabel: {
    color: colors.text.tertiary,
    fontSize: tokens.fontSize.sm,
  },
  kpiValue: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.xl,
    fontWeight: '600',
  },
  kpiHint: {
    color: colors.text.tertiary,
    fontSize: tokens.fontSize.xs,
  },
  sectionTitle: {
    color: colors.text.primary,
  },
  outcomeRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  outcomeLabel: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
  },
  outcomeCount: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    fontWeight: '500',
  },
  outcomePercent: {
    color: colors.text.tertiary,
    fontSize: tokens.fontSize.sm,
  },
  barTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.background.input,
    marginTop: 4,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 3,
  },
});
